import React from "react";
import "@/index.css";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation, Outlet } from "react-router-dom";
import { AuthProvider, useAuth } from "@/lib/auth";
import { Toaster, toast } from "sonner";
import Login from "@/pages/Login";
import AuthCallback from "@/pages/AuthCallback";
import AppLayout from "@/components/AppLayout";
import Dashboard from "@/pages/Dashboard";
import Applications from "@/pages/Applications";
import B2BStudents from "@/pages/B2BStudents";
import B2BStudentDetail from "@/pages/B2BStudentDetail";
import ApplicationDetail from "@/pages/ApplicationDetail";
import MyWork from "@/pages/MyWork";
import WorkReport from "@/pages/WorkReport";
import { Partners, PartnerDetail } from "@/pages/Partners";
import Enrollment from "@/pages/Enrollment";
import Commissions from "@/pages/Commissions";
import Tasks from "@/pages/Tasks";
import Reports from "@/pages/Reports";
import Users from "@/pages/Users";
import Settings from "@/pages/Settings";
import MasterData from "@/pages/MasterData";
import B2CDashboard from "@/pages/B2CDashboard";
import B2CStudents from "@/pages/B2CStudents";
import B2CStudentDetail from "@/pages/B2CStudentDetail";
import B2CApplications from "@/pages/B2CApplications";
import B2CApplicationDetail from "@/pages/B2CApplicationDetail";
import B2CEnrollment from "@/pages/B2CEnrollment";
import B2CCommissions from "@/pages/B2CCommissions";
import B2CImport from "@/pages/B2CImport";
import B2CVarianceReport from "@/pages/B2CVarianceReport";
import B2COfficerHome from "@/pages/B2COfficerHome";
import B2CDuplicates from "@/pages/B2CDuplicates";
import AuditLog from "@/pages/AuditLog";
import ImmigrationApplications from "@/pages/ImmigrationApplications";
import ImmigrationAppDetail from "@/pages/ImmigrationAppDetail";
import ImmigrationNew from "@/pages/ImmigrationNew";
import ImmigrationFilingManager from "@/pages/ImmigrationFilingManager";
import ImmigrationMyFiles from "@/pages/ImmigrationMyFiles";
import ImmigrationAppTypes from "@/pages/ImmigrationAppTypes";
import ImmigrationChecklistTemplates from "@/pages/ImmigrationChecklistTemplates";
import ImmigrationFees from "@/pages/ImmigrationFees";
import ImmigrationIRCCForms from "@/pages/ImmigrationIRCCForms";
import ImmigrationRetainerSettings from "@/pages/ImmigrationRetainerSettings";
import ImmigrationRetainerClauses from "@/pages/ImmigrationRetainerClauses";
import RetainerSign from "@/pages/RetainerSign";
import ImmigrationMyTasks from "@/pages/ImmigrationMyTasks";
import ImmigrationCEODashboard from "@/pages/ImmigrationCEODashboard";
import ImmigrationPerformance from "@/pages/ImmigrationPerformance";
import Client360, { ClientSearch } from "@/pages/Client360";
import LeadsList from "@/pages/LeadsList";
import LeadNew from "@/pages/LeadNew";
import LeadDetail from "@/pages/LeadDetail";
import MyLeads from "@/pages/MyLeads";
import LeadsManagement from "@/pages/LeadsManagement";
import LeadWebhookSetup from "@/pages/LeadWebhookSetup";
import PartnerLogin from "@/pages/PartnerLogin";
import PartnerLayout from "@/pages/PartnerLayout";
import PartnerDashboard from "@/pages/PartnerDashboard";
import PartnerAppDetail from "@/pages/PartnerAppDetail";
import { PartnerAuthProvider } from "@/lib/partnerAuth";
import { Loader2 } from "lucide-react";

function Protected({ children }) {
  const { user, loading } = useAuth();
  if (loading) return (
    <div className="min-h-screen flex items-center justify-center">
      <Loader2 className="w-5 h-5 animate-spin text-slate-500" />
    </div>
  );
  if (!user) return <Navigate to="/login" replace />;
  return children;
}

// LOB gate for frontend routes. If a B2C-only user opens a B2B URL (or vice versa),
// show a "No access" toast and redirect to the user's own dashboard. Admin + BOTH
// users always pass through. Mirrors the backend 403 so the UX never shows a dead
// screen with 403 noise.
function LobGuard({ lob }) {
  const { user } = useAuth();
  const userLob = (user?.line_of_business || "BOTH").toUpperCase();
  const isAdmin = user?.role === "admin";
  const allowed = isAdmin || userLob === "BOTH" || userLob === lob;
  const shownRef = React.useRef(false);
  React.useEffect(() => {
    if (!allowed && !shownRef.current) {
      shownRef.current = true;
      toast.error(`No access — this section is restricted to ${lob} users`);
    }
  }, [allowed, lob]);
  if (!allowed) {
    const home = userLob === "B2B" ? "/dashboard" : userLob === "B2C" ? "/b2c" : "/dashboard";
    return <Navigate to={home} replace />;
  }
  return <Outlet />;
}

function RootRouter() {
  const location = useLocation();
  if (location.hash?.includes("session_id=")) return <AuthCallback />;
  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route path="/partner/login" element={<PartnerAuthProvider><PartnerLogin /></PartnerAuthProvider>} />
      <Route path="/retainer/sign/:token" element={<RetainerSign />} />
      <Route path="/partner" element={<PartnerAuthProvider><PartnerLayout /></PartnerAuthProvider>}>
        <Route index element={<PartnerDashboard />} />
        <Route path="applications/:id" element={<PartnerAppDetail />} />
      </Route>
      <Route path="/" element={<Protected><AppLayout /></Protected>}>
        <Route index element={<Navigate to="/dashboard" replace />} />
        {/* B2B-only routes */}
        <Route element={<LobGuard lob="B2B" />}>
          <Route path="dashboard" element={<Dashboard />} />
          <Route path="students" element={<B2BStudents />} />
          <Route path="students/:id" element={<B2BStudentDetail />} />
          <Route path="applications" element={<Applications />} />
          <Route path="applications/:id" element={<ApplicationDetail />} />
          <Route path="my-work" element={<MyWork />} />
          <Route path="work-report" element={<WorkReport />} />
          <Route path="partners" element={<Partners />} />
          <Route path="partners/:id" element={<PartnerDetail />} />
          <Route path="enrollment" element={<Enrollment />} />
          <Route path="commissions" element={<Commissions />} />
          <Route path="tasks" element={<Tasks />} />
          <Route path="reports" element={<Reports />} />
        </Route>
        {/* B2C-only routes (Client 360 is anchored on b2c_students → B2C-gated too) */}
        <Route element={<LobGuard lob="B2C" />}>
          <Route path="b2c" element={<B2CDashboard />} />
          <Route path="b2c/my-work" element={<B2COfficerHome />} />
          <Route path="b2c/students" element={<B2CStudents />} />
          <Route path="b2c/duplicates" element={<B2CDuplicates />} />
          <Route path="b2c/students/:id" element={<B2CStudentDetail />} />
          <Route path="b2c/applications" element={<B2CApplications />} />
          <Route path="b2c/applications/:id" element={<B2CApplicationDetail />} />
          <Route path="b2c/enrollment" element={<B2CEnrollment />} />
          <Route path="b2c/commissions" element={<B2CCommissions />} />
          <Route path="b2c/import" element={<B2CImport />} />
          <Route path="b2c/variance" element={<B2CVarianceReport />} />
          <Route path="clients" element={<ClientSearch />} />
          <Route path="clients/:id" element={<Client360 />} />
        </Route>
        {/* Immigration + Leads + common — unaffected by LOB rule */}
        <Route path="immigration" element={<ImmigrationMyFiles />} />
        <Route path="immigration/filing-manager" element={<ImmigrationFilingManager />} />
        <Route path="immigration/applications" element={<ImmigrationApplications />} />
        <Route path="immigration/applications/:id" element={<ImmigrationAppDetail />} />
        <Route path="immigration/new" element={<ImmigrationNew />} />
        <Route path="immigration/app-types" element={<ImmigrationAppTypes />} />
        <Route path="immigration/checklist-templates" element={<ImmigrationChecklistTemplates />} />
        <Route path="immigration/fees" element={<ImmigrationFees />} />
        <Route path="immigration/ircc-forms" element={<ImmigrationIRCCForms />} />
        <Route path="immigration/retainer-settings" element={<ImmigrationRetainerSettings />} />
        <Route path="immigration/retainer-clauses" element={<ImmigrationRetainerClauses />} />
        <Route path="immigration/my-tasks" element={<ImmigrationMyTasks />} />
        <Route path="immigration/ceo" element={<ImmigrationCEODashboard />} />
        <Route path="immigration/performance" element={<ImmigrationPerformance />} />
        <Route path="leads" element={<LeadsList />} />
        <Route path="leads/new" element={<LeadNew />} />
        <Route path="leads/mine" element={<MyLeads />} />
        <Route path="leads/reporting" element={<LeadsManagement />} />
        <Route path="leads/webhook-setup" element={<LeadWebhookSetup />} />
        <Route path="leads/:id" element={<LeadDetail />} />
        <Route path="master-data" element={<MasterData />} />
        <Route path="users" element={<Users />} />
        <Route path="audit-log" element={<AuditLog />} />
        <Route path="settings" element={<Settings />} />
      </Route>
      <Route path="*" element={<Navigate to="/dashboard" replace />} />
    </Routes>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <RootRouter />
        <Toaster position="top-right" richColors />
      </AuthProvider>
    </BrowserRouter>
  );
}
